import { useCallback, useEffect, useRef, useState } from 'react';

type Options = {
  onToggle: () => void;
  threshold?: number;
  cooldownMs?: number;
};

export function useShakeToggle({ onToggle, threshold = 15, cooldownMs = 1200 }: Options) {
  const [enabled, setEnabled] = useState(false);
  const last = useRef({ t: 0, ax: 0, ay: 0, az: 0 });
  const cooldownUntil = useRef(0);

  const requestPermission = useCallback(async () => {
    if (typeof window === 'undefined' || !/Mobi|Android/i.test(navigator.userAgent)) {
      return false;
    }
    const DM = (window as any).DeviceMotionEvent;
    if (DM && typeof DM.requestPermission === 'function') {
      try {
        const res = await DM.requestPermission();
        if (res !== 'granted') return false;
      } catch {
        return false;
      }
    }
    setEnabled(true);
    return true;
  }, []);

  useEffect(() => {
    if (!enabled) return;

    const handler = (e: DeviceMotionEvent) => {
      const now = Date.now();
      if (now < cooldownUntil.current) return;

      const acc = e.accelerationIncludingGravity || e.acceleration;
      if (!acc) return;

      const ax = acc.x ?? 0, ay = acc.y ?? 0, az = acc.z ?? 0;

      const dx = Math.abs(ax - last.current.ax);
      const dy = Math.abs(ay - last.current.ay);
      const dz = Math.abs(az - last.current.az);

      const delta = dx + dy + dz;
      last.current = { t: now, ax, ay, az };

      if (delta > threshold) {
        cooldownUntil.current = now + cooldownMs;
        onToggle();
      }
    };

    window.addEventListener('devicemotion', handler, { passive: true });
    return () => window.removeEventListener('devicemotion', handler);
  }, [enabled, onToggle, threshold, cooldownMs]);

  return { enabled, requestPermission };
}